import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { AppContextType, TOSStatusType } from 'types'
import { ApiFactory } from 'apiFactory'
import {
  Orgmanagerv1Org,
  WebToHomeApi,
  NewMoverApi,
  OrganizationsApi,
  TargetsApi,
  NotificationsApi,
  CampaignsApi,
  CreativesApi,
  StatsApi,
  BillingApi,
  AudiencesApi,
  GlobalSearchApi,
  UsersApi,
  TermsOfServiceApi,
  Billingservicev1Org,
  WebToHomePrintPartnerOnlyApi,
} from 'next-gen-sdk'
import { SearchType } from 'Components/MainHeader/components'
import { showErrorMessage } from '@eltoro-ui/components'
import { useMemo } from 'react'
import { useAuth } from 'react-oidc-context'

export type AuthClient = {
  idToken?: string
  refreshToken?: string
  token?: string
}

export const appContextBaseState = {
  currentOrg: undefined,
  setCurrentOrg: () => {},
  refreshCurrentOrg: () => {},
  rebuild: false,
  setRebuild: () => {},
  tok: '',
  setTok: () => {},
  roles: [],
  setRoles: () => {},
  setGroups: () => {},
  searchValue: '',
  setSearchValue: () => {},
  searchType: 'all' as SearchType,
  setSearchType: () => {},
  errorRebuildCount: React.createRef() as React.MutableRefObject<number>,
  refreshAuth: () => Promise.resolve(false),
  isOnHold: false,
  setCurrentBillingOrg: () => {},
  tosStatus: 'loading' as TOSStatusType,
  setTosStatus: () => {},
  overrideCreditCardRequirement: false,
  webToHomeEnabled: false,
  getBrowserTimezone: () => 'UTC',
  timeZone: '',
}

export const AppContext = React.createContext<AppContextType>(
  appContextBaseState,
)

// Export a hook to use context (avoids importing useContext+AppContext every time)
export const useAppContext = () => {
  const ctx = useContext(AppContext)
  if (!ctx)
    throw new Error('useAppContext must be used within the AppContextProvider')
  return ctx
}

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentOrg, setCurrentOrg] = useState<Orgmanagerv1Org>()
  // prettier-ignore
  const [currentBillingOrg, setCurrentBillingOrg] = useState<Billingservicev1Org>()
  const [orgServiceApi, setOrgServiceApi] = useState<OrganizationsApi>()
  const [targetServiceApi, setTargetServiceApi] = useState<TargetsApi>()
  const [notificationsApi, setNotificationsApi] = useState<NotificationsApi>()
  // prettier-ignore
  const [campaignServiceApi, setCampaignServiceApi] = useState<
    CampaignsApi
  >()
  // prettier-ignore
  const [creativeServiceApi, setCreativeServiceApi] = useState<
    CreativesApi
  >()
  // prettier-ignore
  const [statsServiceApi, setStatsServiceApi] = useState<StatsApi>()
  const [webToHomeApi, setWebToHomeApi] = useState<WebToHomeApi>()
  // prettier-ignore
  const [billingServiceApi, setBillingServiceApi] = useState<
    BillingApi
  >()
  // prettier-ignore
  const [audienceServiceApi, setAudienceServiceApi] = useState<
    AudiencesApi
  >()
  // prettier-ignore
  const [webToHomePrintPartnerApi, setWebToHomePrintPartnerApi] = useState<
    WebToHomePrintPartnerOnlyApi
  >()

  const [newMoverApi, setNewMoverApi] = useState<NewMoverApi>()
  const [searchServiceApi, setSearchServiceApi] = useState<GlobalSearchApi>()
  const [userServiceApi, setUserServiceApi] = useState<UsersApi>()
  const [tosApi, setTosApi] = useState<TermsOfServiceApi>()
  const [tok, setTok] = useState<string>('')
  const isMounted = useRef(false)

  // Temp Admin Hack
  // const [admin, setAdmin] = useState<boolean>(false)
  const [rebuild, setRebuild] = useState<boolean>(true)
  const [roles, setRoles] = useState<string[]>()
  const [groups, setGroups] = useState<string[]>()
  // global search menu state
  const [searchValue, setSearchValue] = useState('')
  const [searchType, setSearchType] = useState<SearchType>('all')
  const [orgError, setOrgError] = useState(false)
  const [timeZone, setTimezone] = useState('')
  const auth = useAuth()
  const isAdmin = roles && roles.includes('nextgen_admin')
  const isUser = roles && roles.includes('nextgen_user')
  const isReadOnly = roles && roles.includes('nextgen_read_only')
  // Gates the pre-launch Core Client environment toggle (PLAPP-777).
  const isCoreClientUser = roles && roles.includes('eltoro_client_core')
  const isExternalSales = groups?.includes('/Sales/External')
  const isInternalSales = groups?.includes('/Sales/Internal')
  const isLeadershipOrDev =
    groups?.includes('/Operations/Leadership') || groups?.includes('/Dev')
  const isDevGrouped = groups?.includes('/Dev')
  const webToHomeEnabled = currentOrg?.webToHomeEnabled
  const [
    overrideCreditCardRequirement,
    setOverrideCreditCardRequirement,
  ] = useState(false)

  const determineOverrideCreditCardRequirement = async () => {
    if (currentOrg && currentOrg.billableOrgId && orgServiceApi) {
      await orgServiceApi
        .advertisingPlatformServiceGetOrg(currentOrg.billableOrgId)
        .then((billableOrg: any) => {
          setOverrideCreditCardRequirement(
            billableOrg.overrideCreditCardRequirement,
          )
        })
        .catch(() => {
          setOverrideCreditCardRequirement(
            currentOrg.overrideCreditCardRequirement || false,
          )
        })
    } else {
      setOverrideCreditCardRequirement(
        currentOrg && currentOrg.overrideCreditCardRequirement
          ? currentOrg.overrideCreditCardRequirement
          : false,
      )
    }
  }
  const isOnHold =
    isUser &&
    !isExternalSales &&
    !isInternalSales &&
    (currentOrg?.accountingHold ||
      currentOrg?.adminAccountingHold ||
      currentOrg?.billingHold ||
      (!currentOrg?.setupFeePaid && !currentOrg?.billableOrgId))
  const userId = auth?.user?.profile?.sub
  const [tosStatus, setTosStatus] = useState<TOSStatusType>(
    !isAdmin ? 'loading' : 'signed',
  )

  const query = useMemo(() => new URLSearchParams(window.location.search), [])

  const URLOrg = query.get('org_id')
  // Timezone mapping based on common timezone identifiers
  const timezoneMap: {
    [key: string]: { standard: string; daylight: string }
  } = {
    // Eastern Time
    'America/New_York': { standard: 'EST', daylight: 'EDT' },
    'America/Detroit': { standard: 'EST', daylight: 'EDT' },
    'America/Kentucky/Louisville': {
      standard: 'EST',
      daylight: 'EDT',
    },
    'America/Kentucky/Monticello': {
      standard: 'EST',
      daylight: 'EDT',
    },
    'America/Indiana/Indianapolis': {
      standard: 'EST',
      daylight: 'EDT',
    },
    'America/Indiana/Vincennes': {
      standard: 'EST',
      daylight: 'EDT',
    },
    'America/Indiana/Winamac': { standard: 'EST', daylight: 'EDT' },
    'America/Indiana/Marengo': { standard: 'EST', daylight: 'EDT' },
    'America/Indiana/Petersburg': {
      standard: 'EST',
      daylight: 'EDT',
    },
    'America/Indiana/Vevay': { standard: 'EST', daylight: 'EDT' },

    // Central Time
    'America/Chicago': { standard: 'CST', daylight: 'CDT' },
    'America/Indiana/Tell_City': {
      standard: 'CST',
      daylight: 'CDT',
    },
    'America/Indiana/Knox': { standard: 'CST', daylight: 'CDT' },
    'America/Menominee': { standard: 'CST', daylight: 'CDT' },
    'America/North_Dakota/Center': {
      standard: 'CST',
      daylight: 'CDT',
    },
    'America/North_Dakota/New_Salem': {
      standard: 'CST',
      daylight: 'CDT',
    },
    'America/North_Dakota/Beulah': {
      standard: 'CST',
      daylight: 'CDT',
    },

    // Mountain Time
    'America/Denver': { standard: 'MST', daylight: 'MDT' },
    'America/Boise': { standard: 'MST', daylight: 'MDT' },
    'America/Phoenix': { standard: 'MST', daylight: 'MST' }, // Arizona doesn't observe DST

    // Pacific Time
    'America/Los_Angeles': { standard: 'PST', daylight: 'PDT' },
    'America/Tijuana': { standard: 'PST', daylight: 'PDT' },

    // Alaskan Time
    'America/Anchorage': { standard: 'AKST', daylight: 'AKDT' },
    'America/Juneau': { standard: 'AKST', daylight: 'AKDT' },
    'America/Nome': { standard: 'AKST', daylight: 'AKDT' },
    'America/Sitka': { standard: 'AKST', daylight: 'AKDT' },
    'America/Yakutat': { standard: 'AKST', daylight: 'AKDT' },

    // Hawaiian Time
    // Hawaii does not observe DST;  So duplicate the TZ for both fields
    'Pacific/Honolulu': { standard: 'HST', daylight: 'HST' },

    // Atlantic Time
    'America/Halifax': { standard: 'AST', daylight: 'ADT' },
    'America/Glace_Bay': { standard: 'AST', daylight: 'ADT' },
    'America/Moncton': { standard: 'AST', daylight: 'ADT' },
    'America/Goose_Bay': { standard: 'AST', daylight: 'ADT' },

    // Note: Bering time zones are historical and not commonly used in modern systems
    // but keeping them for completeness if needed
    'America/Adak': { standard: 'HST', daylight: 'HDT' },
  }
  /**
   * Gets the user's browser timezone and returns the abbreviated timezone format
   * @returns {string} The timezone abbreviation (e.g., 'EST', 'PDT', etc.)
   */
  const getBrowserTimezone = (): string => {
    try {
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
      const now = new Date()

      // Check if daylight saving time is currently active
      const isDST = (date: Date, timeZone: string): boolean => {
        const jan = new Date(date.getFullYear(), 0, 1)
        const jul = new Date(date.getFullYear(), 6, 1)
        const janOffset =
          new Intl.DateTimeFormat('en', {
            timeZone,
            timeZoneName: 'long',
          })
            .formatToParts(jan)
            .find((part) => part.type === 'timeZoneName')?.value || ''
        const julOffset =
          new Intl.DateTimeFormat('en', {
            timeZone,
            timeZoneName: 'long',
          })
            .formatToParts(jul)
            .find((part) => part.type === 'timeZoneName')?.value || ''
        const currentOffset =
          new Intl.DateTimeFormat('en', {
            timeZone,
            timeZoneName: 'long',
          })
            .formatToParts(date)
            .find((part) => part.type === 'timeZoneName')?.value || ''

        return currentOffset !== janOffset && currentOffset === julOffset
      }

      const isCurrentlyDST = isDST(now, timeZone)

      const mapping = timezoneMap[timeZone]
      if (mapping) {
        return isCurrentlyDST ? mapping.daylight : mapping.standard
      }

      // Fallback: return the original timezone if no mapping found
      console.warn(`No timezone mapping found for: ${timeZone}`)
      return timeZone
    } catch (error) {
      console.warn(
        'Unable to detect browser timezone, falling back to UTC:',
        error,
      )
      return 'UTC'
    }
  }
  useEffect(() => {
    const tz = getBrowserTimezone()
    if (tz) {
      setTimezone(tz)
    }
  }, [])

  const lastTokenRef = useRef<string>('')

  useEffect(() => {
    if (rebuild) {
      const token =
        auth.user?.access_token || localStorage.getItem('eltoro_token')
      if (token && token !== lastTokenRef.current) {
        lastTokenRef.current = token
        const apis = ApiFactory(token)
        setOrgServiceApi(apis.orgServiceAPI)
        setTargetServiceApi(apis.targetServiceAPI)
        setCreativeServiceApi(apis.creativeServiceAPI)
        setNotificationsApi(apis.notificationsAPI)
        setCampaignServiceApi(apis.campaignServiceAPI)
        setStatsServiceApi(apis.statsServiceAPI)
        setBillingServiceApi(apis.billingServiceAPI)
        setWebToHomeApi(apis.webToHomeAPI)
        setNewMoverApi(apis.newMoverAPI)
        setSearchServiceApi(apis.searchServiceAPI)
        setUserServiceApi(apis.userServiceAPI)
        setAudienceServiceApi(apis.audienceServiceAPI)
        setWebToHomePrintPartnerApi(apis.webToHomePrintPartnerAPI)
        setTosApi(apis.tosApi)
        setTok(token)
        localStorage.setItem('eltoro_token', token)
        setRebuild(false)
      } else if (token === lastTokenRef.current) {
        // Token hasn't changed — skip rebuild to avoid stale-token loops
        setRebuild(false)
      }
    }
  }, [rebuild, auth.user?.access_token])

  useEffect(() => {
    // unable to throw this error within the below useEffect, so throwing it here for the error boundary to catch
    if (orgError) throw new Error('Fetching orgs error')
  }, [orgError])

  useEffect(() => {
    let ignore = false
    const fetchOrgsAndSetFirst = async () => {
      if (!orgServiceApi) return
      // for testing on localhost
      // if (isAdmin && /localhost/.test(window.location.hostname)) {
      //   // return the UAT Team 3 org
      //   const defaultElToroOrg = await orgServiceApi.advertisingPlatformServiceGetOrg(
      //     '8268dc71-8298-4582-b2ff-5688c2863e0d',
      //   )
      //   if (defaultElToroOrg) setCurrentOrg(defaultElToroOrg)
      //   return
      // }
      if (
        isAdmin &&
        (process.env.REACT_APP_ENV === 'production' ||
          /nextgen/.test(window.location.hostname))
      ) {
        // return the ET Pizza org
        const defaultElToroOrg = await orgServiceApi.advertisingPlatformServiceGetOrg(
          '586fc97d1829051225a4662d',
        )
        if (defaultElToroOrg) setCurrentOrg(defaultElToroOrg)
        return
      }
      // get first org in list
      const res = await orgServiceApi
        .advertisingPlatformServiceListOrgs(
          1, // pageSize
          undefined, // pageToken
          'create_time desc', // orderBy
          undefined, // filter
          true, // skipPreload
        )
        .catch((err) => {
          // On 401, attempt a token refresh — the rebuild will retry the fetch
          if (err?.code === 401) {
            refreshAuth()
            return undefined
          }
          setOrgError(true)
          return undefined
        })

      if (res?.orgs && !currentOrg && !ignore) {
        const [firstOrg] = res.orgs
        if (!firstOrg?.id) return
        const fullFirstOrg = await orgServiceApi.advertisingPlatformServiceGetOrg(
          firstOrg.id,
        )
        setCurrentOrg(fullFirstOrg)
      }
    }

    const handleGetInitialOrg = async () => {
      if (!orgServiceApi || orgError) return
      let urlOrgId = ''
      if (
        window.location.pathname.includes('org-settings') ||
        window.location.pathname.includes('child-orgs') ||
        window.location.pathname.includes('billing')
      ) {
        const orgIdFromParams = window.location.pathname
          .split('/')
          .reduce((acc, str) => {
            if (
              str === 'orgs' ||
              !str ||
              str === 'org-settings' ||
              str === 'child-orgs' ||
              str === 'billing'
            )
              return acc
            return str
          }, '')

        if (orgIdFromParams) {
          urlOrgId = orgIdFromParams
        }
      } else if (URLOrg) {
        urlOrgId = URLOrg
      }

      if (urlOrgId) {
        const fetchedOrg = await orgServiceApi
          .advertisingPlatformServiceGetOrg(urlOrgId)
          .catch((err) => {
            if (err?.code === 401) {
              refreshAuth()
              return undefined
            }
            if (err?.body?.message === 'orgNotFound') {
              showErrorMessage(
                'Org Not Found',
                `The organization was not found. Org ID: ${urlOrgId}`,
              )
            } else {
              showErrorMessage(
                `Error getting org ${urlOrgId}`,
                err?.body?.message || '',
              )
            }
            fetchOrgsAndSetFirst()
          })
        if (fetchedOrg && !ignore) {
          setCurrentOrg(fetchedOrg)
        }
      } else {
        fetchOrgsAndSetFirst()
      }
    }

    if (!currentOrg && orgServiceApi && isMounted.current) {
      handleGetInitialOrg()
    } else {
      isMounted.current = true
    }

    return () => {
      ignore = true
    }
  }, [currentOrg, orgServiceApi, URLOrg, query, orgError])

  useEffect(() => {
    // if current org id changes, update the routes
    if (currentOrg?.id) {
      const orgId = currentOrg.id
      const query = new URLSearchParams(window.location.search)
      query.delete('org_id')
      query.append('org_id', orgId)
      // if org-settings
      if (window.location.pathname.includes('org-settings')) {
        query.delete('org_id')
        window.history.replaceState(
          '',
          '',
          `${window.location.origin}/orgs/${orgId}/org-settings?${query}`,
        )
        return
      }
      // if child-orgs
      if (window.location.pathname.includes('child-orgs')) {
        query.delete('org_id')
        window.history.replaceState(
          '',
          '',
          `${window.location.origin}/orgs/${orgId}/child-orgs?${query}`,
        )
        return
      }
      // if billing
      if (window.location.pathname.includes('billing')) {
        query.delete('org_id')
        window.history.replaceState(
          '',
          '',
          `${window.location.origin}/orgs/${orgId}/billing?${query}`,
        )
        return
      }
      // everything else, update org_id
      window.history.replaceState(
        '',
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}`,
      )
    }
  }, [currentOrg?.id])

  // check for billing needs
  useEffect(() => {
    let ignore = false
    determineOverrideCreditCardRequirement()
    if (currentOrg?.id && billingServiceApi && !currentOrg.billableOrgId) {
      billingServiceApi
        .advertisingPlatformServiceGetBillingOrg(currentOrg.id)
        .then((billingOrg) => {
          if (ignore) return
          setCurrentBillingOrg(billingOrg)
        })
    }
    return () => {
      ignore = true
    }
  }, [billingServiceApi, currentOrg, isOnHold])

  // check for tos
  useEffect(() => {
    let ignore = false
    if (
      isAdmin ||
      isInternalSales ||
      isExternalSales ||
      currentOrg?.msaSigned
    ) {
      setTosStatus('signed')
      return
    }
    if (currentOrg?.id && userId) {
      setTosStatus('loading')
      setTimeout(() => {
        const { tosApi } = ApiFactory(auth.user?.access_token)

        tosApi
          ?.advertisingPlatformServiceListTOSAgreements(currentOrg.id, userId)
          .then((res) => {
            if ((res.tosAgreements && res.tosAgreements.length) || !ignore) {
              setTosStatus('signed')
              return
            }
            setTosStatus('not-signed')
          })
          .catch((err) => {
            if (
              err.body?.message === 'could not find tos agreements' &&
              !ignore
            )
              setTosStatus('not-signed')
          })
      }, 1000)
    }
    return () => {
      ignore = true
    }
  }, [currentOrg, isAdmin, isExternalSales, isInternalSales, userId])

  // will refresh current org
  const refreshCurrentOrg = () => {
    if (!orgServiceApi || !currentOrg?.id) return
    orgServiceApi
      .advertisingPlatformServiceGetOrg(currentOrg.id)
      .then((res) => setCurrentOrg(res))
  }

  // Counting how many times the user rebuilds when encountering an error message (preventing rebuild loop when the 401 is not resolved)
  // This is reset to 0 in AuthorizedRoutes when the route changes. (Cannot access router here)
  const errorRebuildCount = useRef<number>(0)

  // Deduplicated token refresh: concurrent 401s share a single refresh attempt.
  // Resolves true on success, false on failure — never rejects, so
  // fire-and-forget callers stay safe while callers that care can branch.
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null)
  const refreshAuth = useCallback((): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current
    refreshPromiseRef.current = auth
      .signinSilent()
      .then(() => {
        // handleUserLoaded in App.tsx will fire, storing the new token and setting rebuild=true
        return true
      })
      .catch(() => {
        auth.removeUser()
        return false
      })
      .finally(() => {
        refreshPromiseRef.current = null
      })
    return refreshPromiseRef.current
  }, [auth])

  const contextValues = {
    currentOrg,
    setCurrentOrg,
    refreshCurrentOrg,
    audienceServiceApi,
    orgServiceApi,
    targetServiceApi,
    creativeServiceApi,
    notificationsApi,
    campaignServiceApi,
    statsServiceApi,
    billingServiceApi,
    webToHomeApi,
    newMoverApi,
    searchServiceApi,
    userServiceApi,
    tosApi,
    setRebuild,
    rebuild,
    errorRebuildCount,
    refreshAuth,
    tok,
    setTok,
    roles,
    setRoles,
    groups,
    setGroups,
    isAdmin,
    isExternalSales,
    isInternalSales,
    isUser,
    isReadOnly,
    isCoreClientUser,
    isLeadershipOrDev,
    isDevGrouped,
    isOnHold,
    searchValue,
    setSearchValue,
    searchType,
    setSearchType,
    currentBillingOrg,
    setCurrentBillingOrg,
    tosStatus,
    setTosStatus,
    overrideCreditCardRequirement,
    webToHomeEnabled,
    webToHomePrintPartnerApi,
    getBrowserTimezone,
    timeZone,
  }
  if (!children) {
    return <></>
  }
  return (
    <AppContext.Provider value={contextValues}>{children}</AppContext.Provider>
  )
}
